// TODO Type 'CalendarDate' is not assignable to type 'DateValue | null | undefined'.
// @ts-nocheck
import { twMerge } from '@florencelabs/fabric-styles';
import { MaterialSymbol } from '@florencelabs/icons';
import { TooltipButton } from '@florencelabs/tooltip';
import { CalendarDate, DateValue } from '@internationalized/date';
import {
  ReactElement,
  createContext,
  useContext,
  useMemo,
  useEffect,
} from 'react';
import {
  CalendarGrid as AriaCalendarGrid,
  DateRangePicker as AriaDateRangePicker,
  DateRangePickerProps as AriaDateRangePickerProps,
  CalendarGridHeader,
  CalendarHeaderCell,
  CalendarGridBody,
  ValidationResult,
  Dialog,
  Group,
  composeRenderProps,
  I18nProvider,
  RangeCalendarStateContext,
} from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { DAYS_RANGE } from '@provider-directory/constants';
import { useAvailabilityWindow } from '@provider-directory/hooks/useAvailabilityWindow';
import { ISearchCalendarProps } from '@provider-directory/hooks/useCalendar';
import {
  CalendarHeading,
  CalendarHeadingButton,
  RangeCalendarCell,
} from '../date-picker/calendar.component';
import { RangeCalendarWrapper, RangeValue } from './date-range-picker-calendar';
import { DateField } from './date-range-picker-date-field';
import { Popover } from './date-range-picker-pop-over';
import {
  ButtonWrapper,
  CustomButtonActions,
  CustomDash,
  GroupWrapper,
} from './date-range-state-wrappers';
import { fieldGroupStyles } from './styles';
import { composeTailwindRenderProps } from './utils';
// Contexts
export const TimeZoneContext = createContext('EST');
export const useTimeZone = (): string => useContext(TimeZoneContext);
// Interfaces
export interface FormValuesDateRangePicker {
  dateRange: {
    start: DateValue;
    end: DateValue;
  };
}
export interface CustomDateRangePickerProps<T extends DateValue>
  extends Omit<AriaDateRangePickerProps<T>, 'isOpen' | 'onChange' | 'value'>,
    Omit<
      ISearchCalendarProps,
      | 'toggleOpen'
      | 'isDesktop'
      | 't'
      | 'timeStampHandler'
      | 'onChange'
      | 'value'
    > {
  children?: any;
  errorMessage?: string | ((validation: ValidationResult) => string);
  cancelCta?: string;
  applyCta?: string;
  toggleOpen: () => void;
  setIsOpen: (isOpen: boolean) => void;
  valueRange: {
    startDate: DateValue | null;
    endDate: DateValue | null;
  };
  onChangeRange: ({
    startDate,
    endDate,
    shouldSetPreviousRange,
  }: {
    startDate: DateValue;
    endDate?: DateValue;
    shouldSetPreviousRange?: boolean;
  }) => void;
}

// Components
const CalendarNextButtonTooltip = ({
  lastFetchableDate,
  messageToken,
}: {
  lastFetchableDate: CalendarDate;
  messageToken?: string;
}): ReactElement => {
  const { t } = useTranslation();
  const maxDays = useAvailabilityWindow();

  const calendarContext = useContext(RangeCalendarStateContext);
  const isLastMonth = useMemo(() => {
    if (!calendarContext) {
      return false;
    }
    return calendarContext.visibleRange.end.compare(lastFetchableDate) >= 0;
  }, [calendarContext, lastFetchableDate]);
  return (
    <>
      {isLastMonth && (
        <TooltipButton
          testIdPrefix={'calendar-next'}
          className={
            'static inset-0 h-fit w-fit translate-y-0 cursor-not-allowed text-gray-950/30 hover:text-gray-950/30'
          }
          tooltipText={t(
            messageToken || 'appointmentSlot.appointmentDate.maxDateTooltip',
            {
              maxDays,
              maxMonths: Math.ceil(maxDays / 30),
            },
          )}
          icon={'chevron_right'}
          displayType={'desktop'}
          iconSize={24}
          ariaLabel={t('aria.moreInfo')}
        />
      )}
      <CalendarHeadingButton
        className={isLastMonth ? 'sr-only' : ''}
        slot={'next'}
      />
    </>
  );
};

const RangeDatePicker = <T extends DateValue>({
  onChangeRange,
  locale,
  todaysDate,
  lastFetchableDate,
  setPreviousRange,
  previousRange,
  timeZone,
  cancelCta,
  applyCta,
  toggleOpen,
  setIsOpen,
  isOpen,
  valueRange,
  children,
  defaultValue,
}: CustomDateRangePickerProps<T>): ReactElement => {
  const onChangeRangeDatePicker = (
    dateValue: RangeValue<DateValue>,
    shouldSetPreviousRange: boolean = false,
  ) => {
    const { start, end } = dateValue;
    let adjustableStartDate = start;
    // endDate should be always <=7 days
    let adjustedEndDate =
      end.compare(start.add({ days: DAYS_RANGE })) > 0
        ? start.add({ days: DAYS_RANGE })
        : end;
    const comparisonMaxDate = lastFetchableDate.compare(end);
    // check if  range endDate is greater than lastfetcheable date
    if (comparisonMaxDate <= 0) {
      adjustableStartDate = adjustableStartDate.subtract({
        days: Math.abs(comparisonMaxDate),
      });
      adjustedEndDate = adjustedEndDate.subtract({
        days: Math.abs(comparisonMaxDate),
      });
    }
    onChangeRange({
      startDate: adjustableStartDate,
      endDate: adjustedEndDate,
      shouldSetPreviousRange,
    });
    return { start: adjustableStartDate, end: adjustedEndDate };
  };
  const nextClickHandler = () => {
    if (valueRange.startDate && valueRange.endDate) {
      let newStartDate = valueRange.startDate.add({
        days: DAYS_RANGE,
      });
      let newEndDate = valueRange.endDate.add({
        days: DAYS_RANGE,
      });
      const comparison = lastFetchableDate.compare(newEndDate);
      if (comparison <= 0) {
        newStartDate = newStartDate.subtract({ days: Math.abs(comparison) });
        newEndDate = newEndDate.subtract({ days: Math.abs(comparison) });
      }
      onChangeRangeDatePicker(
        {
          start: newStartDate,
          end: newEndDate,
        },
        true,
      );
    }
  };
  const previousClickHandler = () => {
    if (valueRange.startDate && valueRange.endDate) {
      let newStartDate = valueRange.startDate.subtract({
        days: DAYS_RANGE,
      });
      let newEndDate = newStartDate.add({
        days: DAYS_RANGE,
      });
      const comparison = newStartDate.compare(todaysDate);
      if (comparison < 0) {
        newStartDate = newStartDate.add({ days: Math.abs(comparison) });
        newEndDate = newStartDate.add({ days: DAYS_RANGE });
      }
      onChangeRangeDatePicker(
        {
          start: newStartDate,
          end: newEndDate,
        },
        true,
      );
    }
  };

  const handleApply = () => {
    if (valueRange.startDate && valueRange.endDate) {
      onChangeRangeDatePicker(
        {
          start: valueRange.startDate,
          end: valueRange.endDate,
        },
        true,
      );
    }
  };

  const handleCancelAndOutsideInteraction = () => {
    if (isOpen && previousRange?.startDate && previousRange.endDate) {
      onChangeRange({
        startDate: previousRange.startDate,
        endDate: previousRange.endDate,
        shouldSetPreviousRange: true,
      });
    }
  };

  const calendarValue = useMemo(() => {
    if (valueRange.startDate && valueRange.endDate) {
      return {
        start: valueRange.startDate,
        end: valueRange.endDate,
      };
    }
    return {
      start: todaysDate,
      end: lastFetchableDate.add({ days: DAYS_RANGE }),
    };
  }, [valueRange.startDate, valueRange.endDate, todaysDate, lastFetchableDate]);
  const disabledNextButton = useMemo(() => {
    if (valueRange.endDate && valueRange.startDate) {
      const comparisonLastDate = lastFetchableDate.compare(valueRange.endDate);
      return comparisonLastDate <= 0;
    }
    return true;
  }, [lastFetchableDate, valueRange.endDate, valueRange.startDate]);

  const disabledPrevButton = useMemo(() => {
    if (valueRange.startDate && valueRange.endDate) {
      const comparisonTodaysDate = valueRange.startDate.compare(todaysDate);
      return comparisonTodaysDate <= 0;
    }
    return true;
  }, [valueRange.startDate, todaysDate, valueRange.endDate]);

  //When resizing from mobile to desktop, the calendar should be set to todays date or selected date
  useEffect(() => {
    if (valueRange.startDate && valueRange.endDate) {
      const { start, end } = onChangeRangeDatePicker({
        start: valueRange.startDate,
        end: valueRange.endDate,
      });
      setPreviousRange({
        startDate: start,
        endDate: end,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <I18nProvider locale={locale}>
      <AriaDateRangePicker
        data-testid={'date-range-picker'}
        isOpen={isOpen}
        aria-label={'DateRangePicker'}
        shouldForceLeadingZeros={true}
        minValue={todaysDate}
        maxValue={lastFetchableDate}
        onOpenChange={setIsOpen}
        defaultValue={
          defaultValue ?? {
            start: todaysDate as T,
            end: lastFetchableDate.add({ days: DAYS_RANGE }) as T,
          }
        }
        shouldCloseOnSelect={false}
        value={calendarValue as RangeValue<T>}
        onChange={(dateValue) => {
          if (!dateValue) {
            return;
          }
          onChangeRangeDatePicker({
            start: dateValue.start,
            end: dateValue.end,
          });
        }}
        className={composeTailwindRenderProps(
          '',
          'inline-flex flex-col justify-start',
        )}
      >
        <Group
          className={composeRenderProps(
            'w-auto min-w-[208px] overflow-visible border-none bg-transparent !outline-none',
            (className, renderProps) =>
              fieldGroupStyles({ ...renderProps, className }),
          )}
        >
          <CustomButtonActions
            ariaLabel={'Previous date range selection'}
            onClick={previousClickHandler}
            disabled={disabledPrevButton}
            className={'mr-5 w-fit p-0 outline-offset-[-3px]'}
            iconSize={20}
          >
            <MaterialSymbol
              name={'chevron_left'}
              aria-hidden={true}
              size={20}
              className={'text-gray-950'}
            />
          </CustomButtonActions>
          <GroupWrapper className={'flex items-center p-0'}>
            <>
              <MaterialSymbol
                className={
                  'mr-1 mt-[-1px] group-aria-expanded/actions:text-primary-600'
                }
                name={'calendar_today'}
                aria-hidden={true}
                size={20}
              />
              <DateField data-testid={'range-start-date'} slot={'start'} />
              <CustomDash />
              <DateField data-testid={'range-end-date'} slot={'end'} />
            </>
          </GroupWrapper>
          <CustomButtonActions
            ariaLabel={'Next date range selection disabled'}
            onClick={nextClickHandler}
            isNextAction={true}
            disabled={disabledNextButton}
            messageToken={'search.maxDate'}
            className={'ml-5 w-fit p-0 outline-offset-[-3px]'}
            iconSize={20}
          >
            <MaterialSymbol
              name={'chevron_right'}
              aria-hidden={true}
              size={20}
              className={'text-gray-950'}
            />
          </CustomButtonActions>
        </Group>
        <Popover
          className={'rounded-lg bg-white'}
          placement={'bottom right'}
          showArrow={true}
          previousRange={previousRange}
          handleCancelAndOutsideInteraction={handleCancelAndOutsideInteraction}
        >
          <Dialog
            aria-label={'Date Range Picker'}
            className={twMerge(
              'relative max-h-[inherit] overflow-auto px-6 pb-0 pt-6 outline outline-0',
              'rounded-lg bg-white shadow',
            )}
          >
            <RangeCalendarWrapper
              data-testid={'date-range-picker-calendar'}
              className={'group flex flex-col gap-1'}
            >
              {children ?? (
                <>
                  <div
                    className={
                      'relative mb-3 flex items-center justify-between'
                    }
                  >
                    <CalendarHeading />
                    <div
                      className={
                        'flex items-center justify-center mobile:gap-x-2 tablet:gap-x-6'
                      }
                    >
                      <CalendarHeadingButton slot={'previous'} />
                      <CalendarNextButtonTooltip
                        messageToken={'search.maxDate'}
                        lastFetchableDate={lastFetchableDate}
                      />
                    </div>
                  </div>
                  <AriaCalendarGrid
                    className={
                      'w-full border-separate border-spacing-y-4 pb-6 [&_td]:px-0'
                    }
                    weekdayStyle={'short'}
                  >
                    <CalendarGridHeader className={'mb-4'}>
                      {(day) => (
                        <CalendarHeaderCell
                          className={'px-2 text-center text-body-medium-medium'}
                        >
                          {day}
                        </CalendarHeaderCell>
                      )}
                    </CalendarGridHeader>
                    <CalendarGridBody>
                      {(date) => (
                        <RangeCalendarCell
                          date={date}
                          timeZone={timeZone}
                          className={'mx-0 w-full text-body-medium'}
                        />
                      )}
                    </CalendarGridBody>
                  </AriaCalendarGrid>
                </>
              )}
            </RangeCalendarWrapper>
            <div
              className={
                'flex w-full items-center justify-end gap-6 border-t border-t-gray-400 bg-white py-4'
              }
            >
              <ButtonWrapper
                variant={'clientLink'}
                className={
                  '!hover:text-neutral-950 !text-neutral-950 outline-none'
                }
                onClick={() => {
                  handleCancelAndOutsideInteraction();
                  toggleOpen();
                }}
                cta={cancelCta}
              />

              <ButtonWrapper
                cta={applyCta}
                className={'!bg-[#153865] !text-white'}
                variant={'clientMain'}
                type={'button'}
                onClick={() => {
                  handleApply();
                  toggleOpen();
                }}
              />
            </div>
          </Dialog>
        </Popover>
      </AriaDateRangePicker>
    </I18nProvider>
  );
};

export default RangeDatePicker;
